import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
} from "reactstrap";
import "../login/mobileNumberVerification.css";
import { toastContainer } from "../../utils/toast";
import { connect } from "react-redux";
import * as url from "../../utils/url";
import axios from "../../axios";
import { withRouter } from "react-router";
import RegistrationResult from "../../components/RegistrationResult";
import RegistrationForm from "../../components/RegistrationForm";
import VerificationCodeEntry from "../../components/VerificationCodeEntry";

const EmployeeNumberEntry = ({ history }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [currentStep, setCurrentStep] = useState("form"); // "form", "verification", "result"
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = window.innerWidth < 768;

  const urlParams = new URLSearchParams(window.location.search);
  const orgId = urlParams.get("orgId");

  const handleTryAgain = () => {
    setSubmissionStatus(null);
    setMessage("");
    setPhoneNumber("");
    setFirstName("");
    setLastName("");
    setCurrentStep("form");
  };

  const handleRequestNewCode = () => {
    // Go back to verification step and resend code
    setSubmissionStatus(null);
    setMessage("");
    setCurrentStep("verification");
    handleResendCode();
  };

  const handleChangeNumber = () => {
    // Go back to form step to allow changing phone number
    setCurrentStep("form");
    setSubmissionStatus(null);
    setMessage("");
  };

  const handleResendCode = () => {
    if (!phoneNumber || !orgId) {
      toastContainer("Missing phone number or organization.", "error");
      return;
    }

    setIsLoading(true);

    const payload = {
      phoneNumber: phoneNumber,
      organizationId: orgId,
      firstName: firstName,
      lastName: lastName,
    };

    axios
      .post(url.qrCodeSendVerification, payload)
      .then(({ data }) => {
        const status = data.data.status;
        if (status === "verification_sent") {
          toastContainer("Verification code resent successfully!", "success");
        } else if (status === "already_registered") {
          setSubmissionStatus("already_registered");
          setMessage("You are already registered with this phone number.");
          setCurrentStep("result");
        }
      })
      .catch((err) => {
        console.error("Error resending code:", err);
        toastContainer("Failed to resend code. Please try again.", "error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmit = function(e) {
    e.preventDefault();
    if (phoneNumber && firstName && lastName && orgId) {
      setIsLoading(true);

      const payload = {
        phoneNumber: phoneNumber,
        organizationId: orgId,
        firstName: firstName,
        lastName: lastName,
      };

      axios
        .post(url.qrCodeSendVerification, payload)
        .then(({ data }) => {
          const status = data.data.status;
          if (status === "verification_sent") {
            // Move to verification step
            setCurrentStep("verification");
          } else if (status === "already_registered") {
            setSubmissionStatus("already_registered");
            setMessage("You are already registered with this phone number.");
            setCurrentStep("result");
          } else {
            setSubmissionStatus("error");
            setMessage(
              "We're unable to process your request at the moment. Please try again shortly."
            );
            setCurrentStep("result");
          }
        })
        .catch((err) => {
          const errorMessage = err.response && err.response.data && err.response.data.errors && err.response.data.errors.message;
          if (errorMessage === "Invalid phone number format") {
            setSubmissionStatus("error");
            setMessage("The number you entered can not be used to register.");
          } else {
            console.error("Unexpected error:", err);
            setSubmissionStatus("error");
            setMessage("An unexpected error occurred.");
          }
          setCurrentStep("result");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      toastContainer(
        "Please enter your first name, last name, and a valid phone number.",
        "error"
      );
    }
  };

  const handleVerifyCode = (verificationCode) => {
    if (!verificationCode || verificationCode.length !== 4) {
      toastContainer("Please enter a valid 4-digit code.", "error");
      return;
    }

    setIsLoading(true);

    const payload = {
      phoneNumber: phoneNumber,
      organizationId: orgId,
      verificationCode: verificationCode,
    };

    axios
      .post(url.qrCodeVerifyAndRegister, payload)
      .then(({ data }) => {
        const status = data.data.status;
        if (status === "created") {
          setSubmissionStatus("success");
          setMessage(" You’ll get occasional texts to stay informed and voice your input, when needed.");
          setCurrentStep("result");
        } else {
          setSubmissionStatus("error");
          setMessage(
            "We're unable to process your request at the moment. Please try again shortly."
          );
          setCurrentStep("result");
        }
      })
      .catch((err) => {
        const errorStatus = err.response && err.response.data && err.response.data.data && err.response.data.data.status;
        const errorMessage = err.response && err.response.data && err.response.data.errors && err.response.data.errors.message;

        if (errorStatus === "expired" || (errorMessage && errorMessage.includes("expired"))) {
          setSubmissionStatus("expired");
          setMessage("Verification code has expired. Please request a new code.");
          setCurrentStep("result");
        } else if (errorStatus === "invalid_code" || (errorMessage && errorMessage.includes("Invalid verification code"))) {
          setSubmissionStatus("invalid_code");
          setMessage("Invalid verification code. Please try again.");
          setCurrentStep("result");
        } else if (errorMessage === "No verification code found. Please request a new code.") {
          setSubmissionStatus("expired");
          setMessage("No verification code found. Please request a new code.");
          setCurrentStep("result");
        } else {
          console.error("Unexpected error:", err);
          setSubmissionStatus("error");
          setMessage("An unexpected error occurred.");
          setCurrentStep("result");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTryAgainForInvalidCode = () => {
    // Go back to verification step to retry entering the code
    setSubmissionStatus(null);
    setMessage("");
    setCurrentStep("verification");
  };

  const renderContent = () => {
    if (currentStep === "result" && submissionStatus) {
      return (
        <RegistrationResult
          status={submissionStatus}
          message={message}
          onTryAgain={
            submissionStatus === "invalid_code"
              ? handleTryAgainForInvalidCode
              : handleTryAgain
          }
          onRequestNewCode={handleRequestNewCode}
        />
      );
    }

    if (currentStep === "verification") {
      return (
        <VerificationCodeEntry
          phoneNumber={phoneNumber}
          onVerify={handleVerifyCode}
          onResendCode={handleResendCode}
          onChangeNumber={handleChangeNumber}
          isLoading={isLoading}
        />
      );
    }

    // Default: form step
    return (
      <RegistrationForm
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        onSubmit={handleSubmit}
      />
    );
  };

  return (
    <div
      className={` ${
        isMobile ? "mfa-setup--mobile mfa-setup--mobile-padding" : "mfa-setup"
      }`}
    >
      <Container className="mfa-container">
        <Row className="justify-content-center">
          <Col xs="12" sm="12" md="8" lg="5">
            <Card
              className={`mfa-card ${
                isMobile ? "border-0 shadow-none bg-transparent" : ""
              }`}
              style={
                isMobile
                  ? { maxHeight: "100vh", overflow: "hidden", width: "100%" }
                  : {}
              }
            >
              <CardBody
                style={{
                  padding: isMobile ? "20px" : "40px",
                  minHeight: isMobile ? "400px" : "450px",
                }}
              >
                {renderContent()}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default connect()(withRouter(EmployeeNumberEntry));
